import { useScroll } from "./useScroll"
import BRAND from "../../../config/brand.json"

// The funnel's scroll progress bar, and the ONE place the page's 60Hz scroll
// state is allowed to live (act:aebf07fa).
//
// THIS IS THE ONE PLACE THE 60Hz RATIONALE IS STATED. useScroll setStates a new
// object every animation frame. While it was called at the Landing page ROOT,
// that re-rendered the entire funnel tree once per frame during every scroll —
// the sign-up form the page exists to get filled in, and SchemaWidget's
// per-frame total/markup/token recomputation with it. None of that showed up in
// a Lighthouse score (the DOM is small); it is an interaction-latency cost, paid
// during exactly the interaction that matters. Scoping the subscription here
// means the per-frame re-render redraws this bar and nothing else.
//
// So: do not lift this state back up, and do not add children here that are
// expensive to render. `spec/contracts/scroll_hook_consumer_spec.rb` holds the
// first half of that; the second is yours. The two children below are static
// divs whose only per-frame work is a style object — no computation, no
// measurement, no layout reads.
//
// Out of flow (`fixed`) so it can never shift layout, and aria-hidden because
// it conveys nothing a screen reader needs. The investigation article does the
// same idea differently — a CSS-only reading meter in
// entrypoints/investigation.css — because that surface is ERB and can use a
// scroll-timeline; this one is a React page, which is why the rAF subscription
// exists at all. They are not synchronized and their heights differ.
//
// THE Z-STACK spans files, so the full ordering is stated here: this bar is
// z-[60] — above the sticky nav (z-50) and the phone share chip (z-40), below
// the preview banner (z-[70]). Landing.jsx's chip comment names its own layer
// against this one. Not to be confused with Inertia's async navigation bar
// (configured in entrypoints/application.jsx), which is also fixed, brand-
// coloured and at the top — that one reports a page LOAD, this one reports
// reading position.
//
// SSR-safe by construction: the funnel landing is server-rendered, and the
// hook's initial state is a static { y: 0, progress: 0 }, so the server and the
// client's first hydration render both emit the parked state below. Never seed
// this from a live window read. What is and is not mechanically guarded is
// stated once, in Reveal.jsx (act:35de96ff): a seed that THROWS under node now
// reds CI; a seed that merely disagrees with the client does not.
//
// ── THE RASPBERRY HEAD (operator, 2026-08-26) ────────────────────────────────
// The warm mark recurs on every page a claimant sees, and this bar is how the
// funnel carries it. Three things about the shape are load-bearing:
//
// 1. THE FILL STOPS 6rem SHORT and the head occupies that 6rem, so the two
//    ABUT rather than overlap. This shipped wrong on the public surface first
//    and the failure was ugly rather than broken: the navy ran to the full
//    width while the raspberry travelled at its own rate, so the navy advanced
//    INTO the head — 48px of overlap at half scroll on a 1440px viewport — and
//    since the head fades and tapers, the navy showed through its gaps and
//    under its slope. Keep the two end values in step or that returns.
//
// 2. WIDTH, not scaleX. This bar used to scale a single div, which is cheaper,
//    but a scale factor cannot express "full width minus 6rem" and it squashes
//    a gradient along with everything else — at 10% scroll a 6rem head would
//    render 0.6rem wide. Two absolutely-positioned children on a fixed frame
//    cost a layout pass on a 4px-tall element; that is the cheap side of the
//    trade, and it is the same one investigation.css makes for the same reason.
//
// 3. THE FLOOR. At rest `progress` is 0, so without a minimum the funnel would
//    show no raspberry at all until someone scrolled — no mark on first paint,
//    which is the one thing this exists to prevent. The head's left is clamped
//    at 0 and its width is fixed, so at rest it renders as the parked wedge the
//    other surfaces show standing still.
//
// The colour comes from the palette SSOT rather than a literal
// (brand_palette_parity_spec bans hex in the JS consumers). It is used as a
// gradient stop and never as text: warm is 3.84:1, which fails the text bar on
// every ground, and the non-text lock in that same spec is what holds it there.
const HEAD = "6rem"
const WARM = BRAND.colors.warm

export default function ScrollProgressBar() {
  const { progress } = useScroll()

  // The seam: where the navy stops and the raspberry starts. One expression,
  // read by both children, so they cannot drift apart.
  const seam = `calc((100% - ${HEAD}) * ${progress})`

  return (
    <div aria-hidden="true" className="fixed top-0 left-0 right-0 h-1 z-[60] pointer-events-none">
      <div className="absolute inset-y-0 left-0 bg-brand" style={{ width: seam }} />
      <div
        className="absolute inset-y-0"
        style={{
          left: seam,
          width: HEAD,
          // Variant 6: solid for 1.2rem, then fading in colour while the clip
          // takes its height away, ending at a point on the leading tip. Both
          // are anchored to the LEFT here because this element IS the head —
          // unlike the article's, which anchors from the right because there
          // the element grows and the mark on it must not.
          background: `linear-gradient(90deg, ${WARM} 0 1.2rem, transparent 100%)`,
          clipPath: "polygon(0 0, 100% 0, 0 100%)",
        }}
      />
    </div>
  )
}
